import { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { claimsAPI } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';

const WORKFLOW = ['submitted', 'under_review', 'pending_approval', 'approved', 'paid'];

const STATUS_META = {
  submitted:        { label: 'Pending Submission', color: 'text-amber-700',   bg: 'bg-amber-50',   border: 'border-amber-300'   },
  under_review:     { label: 'Under Review',       color: 'text-blue-700',    bg: 'bg-blue-50',    border: 'border-blue-300'    },
  pending_approval: { label: 'Board Approval',     color: 'text-violet-700',  bg: 'bg-violet-50',  border: 'border-violet-300'  },
  approved:         { label: 'Approved',           color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-300' },
  rejected:         { label: 'Rejected',           color: 'text-red-700',     bg: 'bg-red-50',     border: 'border-red-300'     },
  paid:             { label: 'Paid',               color: 'text-orange-600',    bg: 'bg-orange-50',    border: 'border-orange-300'    },
};

const TYPE_LABELS = { death: 'Death', disability: 'Disability', other: 'Other' };

export default function ClaimDetail() {
  const { id }       = useParams();
  const { user }     = useAuthStore();
  const navigate     = useNavigate();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [actionError, setActionError] = useState('');
  const [actionBusy, setActionBusy]   = useState('');

  // Board approvals
  const [boardApprovals, setBoardApprovals]   = useState(null);
  const [showBoardDecision, setShowBoardDecision] = useState(false);
  const [boardDecisionType, setBoardDecisionType] = useState('approved');
  const [boardNotes, setBoardNotes]             = useState('');

  // Reject dialog
  const [showReject, setShowReject]   = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // Notes
  const [noteText, setNoteText] = useState('');
  const [noteLoading, setNoteLoading] = useState(false);

  // Documents
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  function load() {
    setLoading(true);
    Promise.all([
      claimsAPI.get(id),
      claimsAPI.getBoardApprovals(id).catch(() => null),
    ]).then(([res, boardRes]) => {
      setData(res);
      if (boardRes) setBoardApprovals(boardRes);
    })
      .catch(() => setError('Failed to load claim'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, [id]);

  async function doAction(key, fn) {
    setActionBusy(key); setActionError('');
    try {
      await fn();
      load();
    } catch (err) {
      setActionError(err.response?.data?.error || `Failed: ${key}`);
    } finally {
      setActionBusy('');
    }
  }

  async function handleUnderReview() {
    doAction('under_review', () => claimsAPI.setUnderReview(id));
  }

  async function handleApprove() {
    doAction('approve', () => claimsAPI.approve(id));
  }

  async function handleReject(e) {
    e.preventDefault();
    setShowReject(false);
    doAction('reject', () => claimsAPI.reject(id, { reason: rejectReason }));
  }

  async function handleMarkPaid() {
    doAction('paid', () => claimsAPI.markAsPaid(id));
  }

  async function handleSendToBoard() {
    doAction('send_board', () => claimsAPI.sendToBoard(id));
  }

  async function handleBoardDecision(e) {
    e.preventDefault();
    setShowBoardDecision(false);
    doAction('board_decision', () => claimsAPI.boardDecision(id, boardDecisionType, boardNotes));
    setBoardNotes('');
  }

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setActionError('');
    const fd = new FormData();
    fd.append('document', file);
    try {
      await claimsAPI.uploadDocument(id, fd);
      load();
    } catch (err) {
      setActionError(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function handleAddNote(e) {
    e.preventDefault();
    if (!noteText.trim()) return;
    setNoteLoading(true);
    try {
      await claimsAPI.addNote(id, noteText.trim());
      setNoteText('');
      load();
    } catch (err) {
      setActionError(err.response?.data?.error || 'Failed to add note');
    } finally {
      setNoteLoading(false);
    }
  }

  if (loading) {
    return <div className="flex items-center justify-center py-20 text-slate-400 text-sm">Loading…</div>;
  }

  if (error || !data) {
    return (
      <div className="text-center py-20">
        <p className="text-red-600 font-medium">{error || 'Claim not found'}</p>
        <Link to="/claims" className="mt-4 inline-block text-sm text-orange-600 hover:underline">← Back to Claims</Link>
      </div>
    );
  }

  const { claim, beneficiaries = [], submitter, reviewer } = data;
  const sm = STATUS_META[claim.status] || STATUS_META.submitted;
  const isRejected  = claim.status === 'rejected';
  const workflowIdx = WORKFLOW.indexOf(claim.status);

  const isAdmin   = user?.role === 'admin';
  const isFinance = user?.role === 'finance';
  const isBoard   = user?.role === 'board';

  // Board member's own approval record
  const myApproval = boardApprovals?.approvals?.find(a => String(a.board_member_id) === String(user?.id));

  return (
    <div className="max-w-4xl mx-auto space-y-5 p-4 sm:p-6">

      {/* Back */}
      <Link to="/claims" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
          <path strokeLinecap="round" strokeLinejoin="round" d="m14.25 18-6-6 6-6" />
        </svg>
        Back to Claims
      </Link>

      {/* Hero */}
      <div className={`rounded-2xl border-2 ${sm.border} ${sm.bg} px-6 py-5`}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-1">Claim</p>
            <h1 className="text-xl font-bold text-slate-900 font-mono">{claim.claim_no}</h1>
            <p className={`mt-1 text-sm font-semibold capitalize ${sm.color}`}>{sm.label}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <InfoChip label="Type"   value={TYPE_LABELS[claim.claim_type] || claim.claim_type} />
            <InfoChip label="Amount" value={claim.total_amount ? `TZS ${Number(claim.total_amount).toLocaleString()}` : '—'} />
            <InfoChip label="Date"   value={claim.date_reported} />
          </div>
        </div>
      </div>

      {/* Action error */}
      {actionError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{actionError}</div>
      )}

      {/* Workflow progress */}
      {!isRejected && (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-4">Progress</h2>
          <div className="flex items-center gap-0">
            {WORKFLOW.map((step, i) => {
              const done    = i <= workflowIdx;
              const current = i === workflowIdx;
              const last    = i === WORKFLOW.length - 1;
              const meta    = STATUS_META[step];
              return (
                <div key={step} className="flex items-center flex-1 min-w-0">
                  <div className="flex flex-col items-center flex-1 min-w-0">
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
                      done
                        ? `border-orange-500 bg-orange-500 text-white`
                        : 'border-slate-200 bg-white text-slate-400'
                    } ${current ? 'ring-4 ring-orange-100' : ''}`}>
                      {done ? (current ? <DotIcon /> : <CheckIcon />) : i + 1}
                    </div>
                    <p className={`mt-1.5 text-center text-[10px] font-semibold leading-tight ${done ? 'text-slate-700' : 'text-slate-400'}`}>
                      {meta?.label}
                    </p>
                  </div>
                  {!last && (
                    <div className={`flex-1 h-0.5 mb-4 mx-1 ${i < workflowIdx ? 'bg-orange-500' : 'bg-slate-200'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Rejection notice */}
      {isRejected && claim.rejection_reason && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-red-500 mb-1">Rejection Reason</p>
          <p className="text-sm text-red-800">{claim.rejection_reason}</p>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-3">

        {/* Left column */}
        <div className="lg:col-span-2 space-y-5">

          {/* Claim details */}
          <Section title="Claim Details">
            <InfoGrid>
              <InfoRow label="Claim Number"    value={claim.claim_no} mono />
              <InfoRow label="Type"            value={TYPE_LABELS[claim.claim_type] || claim.claim_type} />
              <InfoRow label="Date Reported"   value={claim.date_reported} />
              <InfoRow label="Date of Incident" value={claim.date_of_incident || '—'} />
              <InfoRow label="Total Amount"    value={claim.total_amount ? `TZS ${Number(claim.total_amount).toLocaleString()}` : '—'} />
              <InfoRow label="Status"          value={sm.label} />
              {claim.approved_at && <InfoRow label="Approved At" value={claim.approved_at} />}
              {claim.paid_at     && <InfoRow label="Paid At"     value={claim.paid_at} />}
            </InfoGrid>
          </Section>

          {/* Member info */}
          <Section title="Member">
            <InfoGrid>
              <InfoRow label="Full Name" value={claim.member_name || '—'} />
              <InfoRow label="Roll No"   value={claim.member_roll_no || '—'} mono />
              <InfoRow label="Email"     value={claim.member_email || '—'} />
              <InfoRow label="Phone"     value={claim.member_phone || '—'} />
              <InfoRow label="Chapter"   value={claim.member_chapter || '—'} />
              <InfoRow label="Status"    value={claim.member_status || '—'} />
            </InfoGrid>
          </Section>

          {/* Beneficiaries */}
          <Section title={`Beneficiaries (${beneficiaries.length})`}>
            {beneficiaries.length === 0 ? (
              <p className="text-sm text-slate-400 py-4 text-center">No beneficiaries on this claim</p>
            ) : (
              <div className="space-y-3">
                {/* Info hint */}
                {claim.status !== 'paid' && claim.status !== 'rejected' && (isAdmin || isFinance) && (
                  <p className="text-xs text-slate-500 bg-slate-50 rounded-xl px-3 py-2.5 border border-slate-200">
                    Select the beneficiary who will receive the payout by clicking <strong>Select</strong>.
                  </p>
                )}
                {beneficiaries.map((b) => {
                  const isSelected = b.status === 'approved' || b.status === 'paid';
                  const canSelect  = (isAdmin || isFinance) && claim.status !== 'paid' && claim.status !== 'rejected';
                  return (
                    <div key={b.id} className={`rounded-xl border-2 p-4 transition-all ${
                      isSelected
                        ? 'border-emerald-400 bg-emerald-50'
                        : 'border-slate-200 bg-white'
                    }`}>
                      <div className="flex items-start gap-3">
                        {/* Avatar */}
                        <div className={`h-10 w-10 shrink-0 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                          isSelected ? 'bg-emerald-600' : 'bg-slate-500'
                        }`}>
                          {(b.full_name || '?')[0]}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-slate-900 text-sm">{b.full_name || '—'}</p>
                            {isSelected && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold px-2 py-0.5">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="h-3 w-3">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="m5 13 4 4L19 7"/>
                                </svg>
                                Selected
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 capitalize mt-0.5">{(b.relationship || '').toLowerCase()}</p>

                          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mt-3 text-xs">
                            {b.phone  && <BenField label="Phone"        value={b.phone} />}
                            {b.gender && <BenField label="Gender"       value={b.gender} />}
                            {b.address && <BenField label="Address"     value={b.address} />}
                            {b.payment_date && <BenField label="Payment Date" value={b.payment_date} />}
                          </div>
                        </div>

                        {/* Right: amount + action */}
                        <div className="shrink-0 flex flex-col items-end gap-2">
                          <p className={`text-sm font-bold ${isSelected ? 'text-emerald-700' : 'text-slate-700'}`}>
                            TZS {Number(b.amount || 0).toLocaleString()}
                          </p>
                          <BeneficiaryStatusBadge status={b.status} />
                          {canSelect && !isSelected && (
                            <button
                              disabled={!!actionBusy}
                              onClick={() => doAction('select_ben', () => claimsAPI.selectBeneficiary(claim.id, b.id))}
                              className="mt-1 rounded-lg border-2 border-orange-500 px-3 py-1 text-xs font-semibold text-orange-600 hover:bg-orange-500 hover:text-white transition-all disabled:opacity-50">
                              Select
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Section>

          {/* Documents */}
          <Section
            title={`Supporting Documents (${(claim.documents || []).length})`}
            action={
              (isAdmin || isFinance) && (
                <>
                  <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleUpload} className="hidden" />
                  <button
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="rounded-lg bg-slate-800 hover:bg-slate-900 disabled:opacity-60 text-white px-3 py-1.5 text-xs font-medium transition-colors">
                    {uploading ? 'Uploading…' : '+ Upload'}
                  </button>
                </>
              )
            }
          >
            {(!claim.documents || claim.documents.length === 0) ? (
              <p className="text-sm text-slate-400 py-4 text-center">No documents uploaded yet</p>
            ) : (
              <div className="space-y-2">
                {claim.documents.map((doc, i) => (
                  <div key={i} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-white text-xs font-bold ${
                      doc.mime === 'application/pdf' ? 'bg-red-600' : 'bg-blue-600'
                    }`}>
                      {doc.mime === 'application/pdf' ? 'PDF' : 'IMG'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{doc.name}</p>
                      <p className="text-xs text-slate-400">{doc.uploaded_at}</p>
                    </div>
                    <a href={`/${doc.path}`} target="_blank" rel="noreferrer"
                      className="rounded-lg border border-slate-200 bg-white hover:bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors">
                      View
                    </a>
                  </div>
                ))}
              </div>
            )}
          </Section>

        </div>

        {/* Right column */}
        <div className="space-y-5">

          {/* Workflow actions */}
          {(isAdmin || isFinance || isBoard) && (
            <Section title="Actions">
              <div className="space-y-2.5">

                {/* Submitted → Under Review */}
                {claim.status === 'submitted' && (isAdmin || isFinance) && (
                  <ActionButton
                    label="Submit for Review"
                    sub="Upload documents then submit"
                    color="bg-blue-600 hover:bg-blue-700"
                    busy={actionBusy === 'under_review'}
                    onClick={handleUnderReview}
                  />
                )}

                {/* Under Review → Send to Board */}
                {claim.status === 'under_review' && isAdmin && (
                  <>
                    <ActionButton
                      label="Send to Board"
                      sub="Request board member approvals"
                      color="bg-violet-600 hover:bg-violet-700"
                      busy={actionBusy === 'send_board'}
                      onClick={handleSendToBoard}
                    />
                    <ActionButton
                      label="Reject Claim"
                      sub="Provide rejection reason"
                      color="bg-red-600 hover:bg-red-700"
                      busy={actionBusy === 'reject'}
                      onClick={() => setShowReject(true)}
                    />
                  </>
                )}

                {/* Board member decision */}
                {claim.status === 'pending_approval' && isBoard && (
                  myApproval?.decision === 'pending' ? (
                    <button
                      onClick={() => setShowBoardDecision(true)}
                      className="w-full rounded-xl bg-violet-600 hover:bg-violet-700 text-white px-4 py-3 text-left transition-colors">
                      <p className="text-sm font-semibold">Cast Your Vote</p>
                      <p className="text-[11px] text-white/70 mt-0.5">Approve or reject this claim</p>
                    </button>
                  ) : (
                    <div className={`rounded-xl px-4 py-3 text-sm font-semibold ${myApproval?.decision === 'approved' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                      You voted: {myApproval?.decision === 'approved' ? 'Approved ✓' : 'Rejected ✗'}
                    </div>
                  )
                )}

                {/* Admin override when pending_approval */}
                {claim.status === 'pending_approval' && isAdmin && (
                  <>
                    <ActionButton
                      label="Override: Approve"
                      sub="Admin override — bypass board vote"
                      color="bg-emerald-600 hover:bg-emerald-700"
                      busy={actionBusy === 'approve'}
                      onClick={handleApprove}
                    />
                    <ActionButton
                      label="Override: Reject"
                      sub="Admin override — bypass board vote"
                      color="bg-red-600 hover:bg-red-700"
                      busy={actionBusy === 'reject'}
                      onClick={() => setShowReject(true)}
                    />
                  </>
                )}

                {/* Approved → Mark Paid */}
                {claim.status === 'approved' && (isAdmin || isFinance) && (
                  <ActionButton
                    label="Mark as Paid"
                    sub="Confirm payment processed"
                    color="bg-orange-600 hover:bg-orange-500"
                    busy={actionBusy === 'paid'}
                    onClick={handleMarkPaid}
                  />
                )}

                {(claim.status === 'paid' || claim.status === 'rejected') && (
                  <p className="text-xs text-slate-400 text-center py-2">
                    {claim.status === 'paid' ? 'This claim has been paid.' : 'This claim was rejected.'}
                  </p>
                )}
              </div>
            </Section>
          )}

          {/* Board approvals panel */}
          {boardApprovals && boardApprovals.approvals.length > 0 && (
            <Section title={`Board Votes (${boardApprovals.summary.approved}/${boardApprovals.summary.total} approved)`}>
              <div className="space-y-2 mb-3">
                {boardApprovals.approvals.map(a => (
                  <div key={a.id} className={`flex items-center justify-between rounded-xl px-3 py-2.5 border ${
                    a.decision === 'approved' ? 'border-emerald-200 bg-emerald-50' :
                    a.decision === 'rejected' ? 'border-red-200 bg-red-50' :
                    'border-slate-200 bg-slate-50'
                  }`}>
                    <div>
                      <p className="text-xs font-semibold text-slate-800">{a.board_member_name}</p>
                      {a.notes && <p className="text-[10px] text-slate-500 mt-0.5">{a.notes}</p>}
                    </div>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                      a.decision === 'approved' ? 'bg-emerald-600 text-white' :
                      a.decision === 'rejected' ? 'bg-red-600 text-white' :
                      'bg-slate-200 text-slate-500'
                    }`}>
                      {a.decision === 'pending' ? 'Pending' : a.decision === 'approved' ? 'Approved ✓' : 'Rejected ✗'}
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex gap-3 text-center text-xs">
                <div className="flex-1 rounded-lg bg-emerald-50 border border-emerald-200 py-2">
                  <p className="font-bold text-emerald-700 text-base">{boardApprovals.summary.approved}</p>
                  <p className="text-emerald-600">Approved</p>
                </div>
                <div className="flex-1 rounded-lg bg-red-50 border border-red-200 py-2">
                  <p className="font-bold text-red-700 text-base">{boardApprovals.summary.rejected}</p>
                  <p className="text-red-600">Rejected</p>
                </div>
                <div className="flex-1 rounded-lg bg-slate-50 border border-slate-200 py-2">
                  <p className="font-bold text-slate-700 text-base">{boardApprovals.summary.pending}</p>
                  <p className="text-slate-500">Pending</p>
                </div>
              </div>
              <p className="text-[10px] text-slate-400 text-center mt-2">
                Majority needed: {boardApprovals.summary.majority} of {boardApprovals.summary.total} votes
              </p>
            </Section>
          )}

          {/* Processing info */}
          <Section title="Processing Info">
            <InfoGrid>
              {submitter && <InfoRow label="Submitted By" value={submitter.full_name} />}
              {reviewer  && <InfoRow label="Reviewed By"  value={reviewer.full_name} />}
              <InfoRow label="Created" value={claim.created_at?.split(' ')[0] || '—'} />
              {claim.approved_at && <InfoRow label="Approved" value={claim.approved_at?.split(' ')[0]} />}
              {claim.paid_at     && <InfoRow label="Paid"     value={claim.paid_at?.split(' ')[0]} />}
            </InfoGrid>
          </Section>

          {/* Notes */}
          <Section title="Notes">
            <div className="space-y-3">
              {(!claim.notes || claim.notes.length === 0) ? (
                <p className="text-xs text-slate-400 text-center py-2">No notes yet</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {claim.notes.map((n, i) => (
                    <div key={i} className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5">
                      <p className="text-xs text-slate-700">{n.text}</p>
                      <p className="text-[10px] text-slate-400 mt-1">{n.by_name || 'Unknown'} · {n.at || ''}</p>
                    </div>
                  ))}
                </div>
              )}

              {(isAdmin || isFinance) && (
                <form onSubmit={handleAddNote} className="flex gap-2">
                  <input type="text" value={noteText} onChange={e => setNoteText(e.target.value)}
                    placeholder="Add a note…"
                    className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-orange-500" />
                  <button type="submit" disabled={noteLoading || !noteText.trim()}
                    className="rounded-xl bg-slate-800 hover:bg-slate-900 disabled:opacity-50 text-white px-3 py-2 text-xs font-medium transition-colors">
                    {noteLoading ? '…' : 'Add'}
                  </button>
                </form>
              )}
            </div>
          </Section>

        </div>
      </div>

      {/* Board Decision Dialog */}
      {showBoardDecision && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setShowBoardDecision(false)} />
          <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6">
            <h3 className="text-base font-bold text-slate-800 mb-1">Cast Your Vote</h3>
            <p className="text-xs text-slate-500 mb-4">Your vote will be recorded. Majority approval triggers automatic claim approval.</p>
            <form onSubmit={handleBoardDecision} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setBoardDecisionType('approved')}
                  className={`rounded-xl border-2 py-3 text-sm font-semibold transition-all ${boardDecisionType === 'approved' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-500 hover:border-emerald-300'}`}>
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() => setBoardDecisionType('rejected')}
                  className={`rounded-xl border-2 py-3 text-sm font-semibold transition-all ${boardDecisionType === 'rejected' ? 'border-red-500 bg-red-50 text-red-700' : 'border-slate-200 text-slate-500 hover:border-red-300'}`}>
                  Reject
                </button>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1.5">Notes (optional)</label>
                <textarea rows={2} value={boardNotes} onChange={e => setBoardNotes(e.target.value)}
                  placeholder="Add remarks about your decision…"
                  className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none" />
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowBoardDecision(false)}
                  className="flex-1 rounded-xl border border-slate-200 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
                  Cancel
                </button>
                <button type="submit" disabled={actionBusy === 'board_decision'}
                  className={`flex-1 rounded-xl disabled:opacity-60 text-white py-2.5 text-sm font-semibold ${boardDecisionType === 'approved' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}`}>
                  {actionBusy === 'board_decision' ? 'Submitting…' : `Submit ${boardDecisionType === 'approved' ? 'Approval' : 'Rejection'}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reject Dialog */}
      {showReject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setShowReject(false)} />
          <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6">
            <h3 className="text-base font-bold text-slate-800 mb-4">Reject Claim</h3>
            <form onSubmit={handleReject} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1.5">Reason *</label>
                <textarea rows={3} value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                  required placeholder="Explain why this claim is being rejected…"
                  className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-none" />
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowReject(false)}
                  className="flex-1 rounded-xl border border-slate-200 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
                  Cancel
                </button>
                <button type="submit" disabled={actionBusy === 'reject'}
                  className="flex-1 rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white py-2.5 text-sm font-semibold">
                  {actionBusy === 'reject' ? 'Rejecting…' : 'Reject'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="h-4 w-4">
      <path strokeLinecap="round" strokeLinejoin="round" d="m5 13 4 4L19 7" />
    </svg>
  );
}

function DotIcon() {
  return <span className="h-2.5 w-2.5 rounded-full bg-white" />;
}

function Section({ title, children, action }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 bg-slate-50">
        <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-500">{title}</h2>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function InfoGrid({ children }) {
  return <div className="rounded-xl border border-slate-100 overflow-hidden divide-y divide-slate-100">{children}</div>;
}

function InfoRow({ label, value, mono = false }) {
  return (
    <div className="flex items-center justify-between px-3.5 py-2.5 bg-white hover:bg-slate-50 transition-colors">
      <span className="text-xs text-slate-500 font-medium shrink-0">{label}</span>
      <span className={`text-xs font-semibold text-slate-800 text-right ml-4 break-all ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );
}

function InfoChip({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
      <p className="text-[10px] uppercase tracking-widest text-slate-400">{label}</p>
      <p className="text-sm font-semibold text-slate-800 mt-0.5">{value}</p>
    </div>
  );
}

function BenField({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">{label}</p>
      <p className="text-xs font-semibold text-slate-700 mt-0.5">{value}</p>
    </div>
  );
}

function BeneficiaryStatusBadge({ status }) {
  const map = {
    pending:  'bg-amber-100 text-amber-700',
    approved: 'bg-emerald-100 text-emerald-700',
    paid:     'bg-orange-100 text-orange-600',
    rejected: 'bg-red-100 text-red-700',
  };
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold capitalize ${map[status] || 'bg-slate-100 text-slate-600'}`}>
      {status}
    </span>
  );
}

function ActionButton({ label, sub, color, busy, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={!!busy}
      className={`w-full rounded-xl ${color} disabled:opacity-60 text-white px-4 py-3 text-left transition-colors`}
    >
      <p className="text-sm font-semibold">{busy ? 'Processing…' : label}</p>
      {!busy && <p className="text-[11px] text-white/70 mt-0.5">{sub}</p>}
    </button>
  );
}
